import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import {
  Bell,
  ChevronDown,
  Command,
  Menu,
  Megaphone as MegaphoneIcon,
  Search,
  ShoppingBag,
  UserRound,
  Wallet,
  X,
  ArrowUp,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import type { Category } from "@/lib/types";
import logoLight from "@/assets/gitsploit-light.png";
import { MatrixBackground } from "./MatrixBackground";
import { Badge, Button } from "./ui";

export function LogoButton({ className = "" }: { className?: string }) {
  const clicks = useRef(0);
  const timer = useRef<number | undefined>(undefined);
  const navigate = useNavigate();
  const { user } = useAuth();
  const click = () => {
    clicks.current += 1;
    window.clearTimeout(timer.current);
    if (clicks.current >= 3) {
      clicks.current = 0;
      sessionStorage.setItem("gitsploit_staff_gate", String(Date.now()));
      navigate(
        user?.role === "master"
          ? "/master"
          : user?.role === "admin"
            ? "/admin"
            : "/staff-access",
      );
      return;
    }
    timer.current = window.setTimeout(() => {
      if (clicks.current === 1) navigate("/");
      clicks.current = 0;
    }, 430);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <button
      onClick={click}
      className={`logo-button ${className}`}
      aria-label="GitSploit home"
    >
      <img src={logoLight} alt="GitSploit" />
    </button>
  );
}

function CommandPalette({ open, onClose }: { open: boolean; onClose(): void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const links = [
    "Home:/",
    "Marketplace:/marketplace",
    "Categories:/categories",
    "Dashboard:/dashboard",
    "Wallet:/wallet",
    "Global Chat:/chat",
    "Support:/support",
    "About:/about",
  ];
  const shown = links.filter((x) => x.toLowerCase().includes(q.toLowerCase()));
  useEffect(() => {
    if (open) setQ("");
  }, [open]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="command-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={onClose}
        >
          <motion.div
            className="command-panel"
            initial={{ opacity: 0, scale: 0.96, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="command-input">
              <Search size={18} />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search pages or marketplace..."
              />
              <kbd>ESC</kbd>
            </div>
            <div className="command-results">
              {shown.map((item) => {
                const [label, path] = item.split(":");
                return (
                  <button
                    key={path}
                    onClick={() => {
                      navigate(path!);
                      onClose();
                    }}
                  >
                    <span>{label}</span>
                    <Command size={15} />
                  </button>
                );
              })}
              <button
                onClick={() => {
                  navigate(`/search?q=${encodeURIComponent(q)}`);
                  onClose();
                }}
              >
                <span>Search marketplace for “{q}”</span>
                <Search size={15} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);
  const [palette, setPalette] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });
  const categories = useQuery({
    queryKey: ["categories-nav"],
    queryFn: () => api<{ categories: Category[] }>("/categories"),
    staleTime: 300000,
  });
  const site = useQuery({
    queryKey: ["site-settings"],
    queryFn: () =>
      api<{
        settings: {
          site_notice?: { active: boolean; message: string; tone: string };
          maintenance_mode?: boolean;
        };
      }>("/site-settings"),
    refetchInterval: 30000,
  });
  const notifications = useQuery({
    queryKey: ["notification-badge", user?.id],
    queryFn: () => api<{ unread: number }>("/notifications"),
    enabled: !!user,
    refetchInterval: 15000,
  });
  const walletBalance = useQuery({
    queryKey: ["nav-wallet", user?.id],
    queryFn: () => api<{ balance: number }>("/wallet?limit=1"),
    enabled: !!user,
    refetchInterval: 15000,
  });
  useEffect(() => {
    setMobile(false);
    scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((v) => !v);
      }
      if (e.key === "Escape") setPalette(false);
    };
    const scroll = () => setShowTop(scrollY > 600);
    addEventListener("keydown", key);
    addEventListener("scroll", scroll, { passive: true });
    return () => {
      removeEventListener("keydown", key);
      removeEventListener("scroll", scroll);
    };
  }, []);
  const nav = [
    ["Home", "/"],
    ["Marketplace", "/marketplace"],
    ["Categories", "/categories"],
    ["Global chat", "/chat"],
    ["About", "/about"],
  ];
  return (
    <div className="app-shell">
      <MatrixBackground />
      <motion.div className="scroll-progress" style={{ scaleX }} />
      <header className="navbar">
        <div className="nav-inner">
          <LogoButton />
          <nav className="desktop-nav">
            {nav.map(([label, path]) => (
              <div
                className={label === "Marketplace" ? "mega-trigger" : ""}
                key={path}
              >
                <NavLink
                  to={path}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  {label}
                  {label === "Marketplace" && <ChevronDown size={13} />}
                </NavLink>
                {label === "Marketplace" && (
                  <div className="mega-menu">
                    <div>
                      <span className="eyebrow">Explore games</span>
                      <h3>Find your next edge.</h3>
                      <p>
                        Curated tools, transparent seller history, community
                        ratings.
                      </p>
                      <Link to="/marketplace">Browse everything →</Link>
                    </div>
                    <div className="mega-grid">
                      {categories.data?.categories.slice(0, 10).map((c) => (
                        <Link key={c.id} to={`/categories/${c.slug}`}>
                          {c.name}
                          <small>{c.listings || 0} listings</small>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="nav-actions">
            <button className="search-trigger" onClick={() => setPalette(true)}>
              <Search size={16} />
              <span>Search</span>
              <kbd>⌘K</kbd>
            </button>
            {user ? (
              <>
                <Link
                  className="nav-icon"
                  to="/notifications"
                  aria-label="Notifications"
                >
                  <Bell size={19} />
                  {(notifications.data?.unread || 0) > 0 && (
                    <i>{notifications.data!.unread}</i>
                  )}
                </Link>
                <Link
                  className="nav-wallet-balance"
                  to="/wallet"
                  aria-label={`Wallet balance $${Number(walletBalance.data?.balance ?? user.balance).toFixed(2)}`}
                >
                  <Wallet size={19} />
                  <span>
                    <small>Wallet</small>
                    <b>
                      $
                      {Number(
                        walletBalance.data?.balance ?? user.balance,
                      ).toFixed(2)}
                    </b>
                  </span>
                </Link>
                <div className="account-menu">
                  <button>
                    <span className="avatar-mini">
                      {user.username[0].toUpperCase()}
                    </span>
                    <span className="account-name">{user.username}</span>
                    <ChevronDown size={13} />
                  </button>
                  <div>
                    <Link to="/dashboard">Dashboard</Link>
                    <Link to="/profile">Profile</Link>
                    <Link to="/settings">Settings</Link>
                    {user.role === "admin" && (
                      <Link to="/admin">Moderation</Link>
                    )}
                    {user.role === "master" && <Link to="/master">Master</Link>}
                    <button
                      onClick={() => void logout().then(() => navigate("/"))}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link className="login-link" to="/login">
                  Log in
                </Link>
                <Button onClick={() => navigate("/register")}>
                  Join marketplace
                </Button>
              </>
            )}
            <button
              className="mobile-toggle"
              onClick={() => setMobile((v) => !v)}
            >
              {mobile ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {mobile && (
            <motion.div
              className="mobile-nav"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              {nav.map(([label, path]) => (
                <Link key={path} to={path}>
                  {label}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to="/dashboard">Dashboard</Link>
                  <Link to="/wallet">Wallet</Link>
                  <Link to="/notifications">Notifications</Link>
                  <Link to="/profile">Profile</Link>
                  <Link to="/settings">Settings</Link>
                  {user.role === "admin" && <Link to="/admin">Moderation</Link>}
                  {user.role === "master" && (
                    <Link to="/master">Master controls</Link>
                  )}
                  <button
                    onClick={() => void logout().then(() => navigate("/"))}
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login">Log in</Link>
                  <Link to="/register">Register</Link>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      <div
        className={`news-ticker ${site.data?.settings.site_notice?.active ? site.data.settings.site_notice.tone || "info" : "info"}`}
        role="status"
        aria-label="GitSploit news"
      >
        <strong>
          <MegaphoneIcon /> GitSploit live
        </strong>
        <div className="ticker-window">
          <div className="ticker-track">
            {[0, 1].map((copy) => (
              <span key={copy} aria-hidden={copy === 1}>
                <b>Welcome to GitSploit</b>
                <i />
                Gaming exploits, mods, cheats, scripts, and specialist tools
                <i />
                {site.data?.settings.site_notice?.active &&
                site.data.settings.site_notice.message
                  ? site.data.settings.site_notice.message
                  : "Browse new marketplace releases and buy securely on-site"}
                <i />
              </span>
            ))}
          </div>
        </div>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={location.pathname} className="route-wrap">
          <Outlet />
        </motion.div>
      </AnimatePresence>
      <footer>
        <div className="footer-top">
          <div>
            <LogoButton className="footer-logo" />
            <p>
              A professional marketplace built around quality, fairness, and
              security for the gaming community.
            </p>
          </div>
          <div>
            <strong>Marketplace</strong>
            <Link to="/marketplace">Discover</Link>
            <Link to="/categories">Categories</Link>
            <Link to="/search">Search</Link>
            <Link to="/sell/new">Sell</Link>
          </div>
          <div>
            <strong>Community</strong>
            <Link to="/chat">Global chat</Link>
            <Link to="/support">Support</Link>
            <Link to="/about">About us</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <div>
            <strong>Legal</strong>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
          </div>
        </div>
        <div className="footer-bottom">
          <span>
            © {new Date().getFullYear()} GitSploit. A Citadel Consortium
            company.
          </span>
          <a
            href="https://citadelconsortium.com"
            target="_blank"
            rel="noreferrer"
          >
            citadelconsortium.com
          </a>
        </div>
      </footer>
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      {showTop && (
        <motion.button
          className="back-top"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          onClick={() => scrollTo({ top: 0, behavior: "smooth" })}
        >
          <ArrowUp />
        </motion.button>
      )}
    </div>
  );
}

export function Protected({
  roles,
}: {
  roles?: Array<"user" | "admin" | "master">;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="full-loader">
        <div className="loader-g">G</div>
        <span>Securing connection</span>
      </div>
    );
  if (!user) return <NavigateLogin to="/login" from={location.pathname} />;
  if (roles && !roles.includes(user.role)) return <NavigateLogin to="/" />;
  return <Outlet />;
}
function NavigateLogin({ to, from }: { to: string; from?: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace: true, state: { from } });
  }, [navigate, to, from]);
  return null;
}
